import React from 'react';
import { observer } from 'mobx-react-lite';
import {
   Box,
   Callout,
   Flex,
   Heading,
   IconButton,
   ScrollArea,
   Table,
   Text,
   TextField,
   Tooltip,
} from '@radix-ui/themes';
import { useSearchParams } from 'react-router-dom';

import APNavBar from 'Components/adminpanel/Elements/APNavBar';
import { AdminSideBar } from 'admin/elements/side-bar';
import { Paginator } from 'shared/components/pagination';
import { InfoOutlineIcon } from 'shared/icons';
import { TransactionSortKey } from './types';
import { TransactionActionsMenu } from './transaction-actions-menu';
import { TransactionDetailsDialog } from './transaction-details-dialog';
import { TransactionFilters } from './transaction-filters';
import { useTransactions } from './transactions.provider';

import './transactions.scss';

const SKELETON_ROWS = 12;

const renderValue = (value: string | number | null | undefined) => value ?? '-';

const formatPrice = (price: number | null) => {
   if (price === null || Number.isNaN(Number(price))) {
      return '0';
   }

   return new Intl.NumberFormat('uk-UA').format(price);
};

const sortLabel = (active: boolean, direction: 'asc' | 'desc') => {
   if (!active) {
      return '';
   }

   return direction === 'asc' ? ' ^' : ' v';
};

function TransactionsView() {
   const transactionsStore = useTransactions();
   const [searchParams, setSearchParams] = useSearchParams();
   const initFromQueryDone = React.useRef(false);
   const skeletonRows = Array.from({ length: SKELETON_ROWS }, (_, index) => index);

   React.useEffect(() => {
      if (initFromQueryDone.current) {
         return;
      }
      initFromQueryDone.current = true;

      const rawPage = searchParams.get('page');
      const parsedPage = Number(rawPage);
      const page = Number.isFinite(parsedPage) && parsedPage >= 1 ? parsedPage : 1;
      void transactionsStore.loadPage(page - 1);
   }, [searchParams, transactionsStore]);

   React.useEffect(() => {
      const nextPage = String(transactionsStore.pagination.page + 1);
      if (searchParams.get('page') === nextPage) {
         return;
      }

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('page', nextPage);
      setSearchParams(nextSearchParams, { replace: true });
   }, [searchParams, setSearchParams, transactionsStore.pagination.page]);

   const renderSortHeader = (label: string, key: TransactionSortKey, className?: string) => (
      <Table.ColumnHeaderCell className={className}>
         <button
            className="transactions__sort-button"
            type="button"
            onClick={() => {
               void transactionsStore.onSortBy(key);
            }}
         >
            {label}
         </button>
      </Table.ColumnHeaderCell>
   );

   const renderCompanyHeader = () => (
      <Table.ColumnHeaderCell className="transactions__company-cell">
         <button
            className="transactions__sort-button"
            type="button"
            onClick={() => {
               void transactionsStore.onSortBy('company_name');
            }}
         >
            Company Name
            {sortLabel(transactionsStore.sorter === 'company_name', transactionsStore.direction)}
         </button>
      </Table.ColumnHeaderCell>
   );

   return (
      <>
         <APNavBar />
         <Box pt="80px" className="transactions">
            <Flex width="270px" minWidth="270px">
               <AdminSideBar />
            </Flex>

            <Box className="transactions__content">
               <Flex py="1" mx="3" justify="between" align="center" gap="3">
                  <Heading as="h1" weight="medium">
                     Transaction List
                  </Heading>

                  <Flex align="center" gap="2">
                     <Text size="3" color="gray">
                        Records:
                     </Text>
                     <Text size="3" weight="medium">
                        {transactionsStore.recordStart} - {transactionsStore.recordEnd} of{' '}
                        {transactionsStore.totalCount}
                     </Text>
                  </Flex>
               </Flex>

               <TransactionFilters />

               <ScrollArea scrollbars="both" type="always" className="transactions__scroll-area">
                  <Box p="3" className="transactions__table-wrap">
                     <Table.Root
                        size="3"
                        layout="fixed"
                        variant="surface"
                        className="transactions__table"
                     >
                        <Table.Header>
                           <Table.Row>
                              {renderCompanyHeader()}
                              {renderSortHeader(
                                 'Company ID',
                                 'employer_id',
                                 'transactions__id-cell'
                              )}
                              {renderSortHeader(
                                 'Full Name',
                                 'full_name',
                                 'transactions__name-cell'
                              )}
                              {renderSortHeader(
                                 'Manager',
                                 'current_grc_manager',
                                 'transactions__manager-cell'
                              )}
                              {renderSortHeader(
                                 'Plan Name',
                                 'plan_name',
                                 'transactions__plan-cell'
                              )}
                              {renderSortHeader('Price', 'price', 'transactions__price-cell')}
                              {renderSortHeader(
                                 'Request Date',
                                 'created',
                                 'transactions__date-cell'
                              )}
                              {renderSortHeader(
                                 'Activation Date',
                                 'start_date',
                                 'transactions__date-cell'
                              )}
                              {renderSortHeader(
                                 'Expire Date',
                                 'end_date',
                                 'transactions__date-cell'
                              )}
                              <Table.ColumnHeaderCell className="transactions__action-cell">
                                 Action
                              </Table.ColumnHeaderCell>
                           </Table.Row>
                        </Table.Header>

                        <Table.Body>
                           {transactionsStore.loading &&
                              skeletonRows.map((row) => (
                                 <Table.Row
                                    key={`skeleton-${row}`}
                                    className="transaction-skeleton-row"
                                 >
                                    {Array.from({ length: 10 }, (_, cell) => (
                                       <Table.Cell key={`skeleton-${row}-${cell}`}>
                                          <div className="transaction-skeleton" />
                                       </Table.Cell>
                                    ))}
                                 </Table.Row>
                              ))}

                           {!transactionsStore.loading &&
                              transactionsStore.transactions.length === 0 && (
                                 <Table.Row>
                                    <Table.Cell colSpan={10} align="center">
                                       No records found
                                    </Table.Cell>
                                 </Table.Row>
                              )}

                           {!transactionsStore.loading &&
                              transactionsStore.transactions.map((transaction) => (
                                 <Table.Row key={transaction.id}>
                                    <Table.Cell className="transactions__company-cell">
                                       {renderValue(transaction.company_name)}
                                    </Table.Cell>
                                    <Table.Cell className="transactions__id-cell">
                                       {renderValue(transaction.employer_id)}
                                    </Table.Cell>
                                    <Table.Cell className="transactions__name-cell">
                                       {renderValue(transaction.full_name)}
                                    </Table.Cell>
                                    <Table.Cell className="transactions__manager-cell">
                                       {renderValue(transaction.current_grc_manager)}
                                    </Table.Cell>
                                    <Table.Cell className="transactions__plan-cell">
                                       <Callout.Root
                                          variant="soft"
                                          color="blue"
                                          size="1"
                                          className="transactions__plan-callout"
                                       >
                                          <Callout.Icon>
                                             <Tooltip content="Plan details">
                                                <IconButton
                                                   type="button"
                                                   variant="ghost"
                                                   color="blue"
                                                   size="1"
                                                   className="transactions__plan-info-trigger"
                                                   aria-label="Open plan details"
                                                   onClick={() => {
                                                      transactionsStore.openTransactionDialog(
                                                         transaction
                                                      );
                                                   }}
                                                >
                                                   <InfoOutlineIcon width={16} height={16} />
                                                </IconButton>
                                             </Tooltip>
                                          </Callout.Icon>
                                          <Callout.Text className="transactions__plan-callout-text">
                                             {renderValue(transaction.plan_name)}
                                          </Callout.Text>
                                       </Callout.Root>
                                    </Table.Cell>
                                    <Table.Cell className="transactions__price-cell">
                                       {formatPrice(transaction.price)}
                                    </Table.Cell>
                                    <Table.Cell className="transactions__date-cell">
                                       {renderValue(transaction.created)}
                                    </Table.Cell>
                                    <Table.Cell className="transactions__date-cell">
                                       {renderValue(transaction.start_date)}
                                    </Table.Cell>
                                    <Table.Cell className="transactions__date-cell">
                                       {renderValue(transaction.end_date)}
                                    </Table.Cell>
                                    <Table.Cell
                                       justify="center"
                                       className="transactions__action-cell"
                                    >
                                       <TransactionActionsMenu transaction={transaction} />
                                    </Table.Cell>
                                 </Table.Row>
                              ))}
                        </Table.Body>
                     </Table.Root>
                  </Box>
               </ScrollArea>

               <Flex
                  className="transactions__pagination"
                  py="2"
                  px="3"
                  justify="between"
                  align="center"
                  gap="3"
               >
                  <Box />

                  <Paginator
                     totalPages={transactionsStore.totalPages}
                     currentPage={transactionsStore.pagination.page}
                     disabled={transactionsStore.loading}
                     onPageClick={(page) => {
                        void transactionsStore.loadPage(page);
                     }}
                     setRange={(range) => {
                        transactionsStore.pagination.setRange(range);
                     }}
                     range={transactionsStore.pagination.range}
                  />

                  <Flex align="center" gap="2">
                     <Flex align="center" gap="1">
                        <Text size="3" color="gray">
                           Total pages:
                        </Text>
                        <Text size="3" weight="medium">
                           {transactionsStore.totalPages === 0 ? '' : transactionsStore.totalPages}
                        </Text>
                     </Flex>

                     <TextField.Root
                        className="transactions__page-input"
                        value={transactionsStore.toPage}
                        disabled={transactionsStore.loading}
                        onChange={(e) => {
                           transactionsStore.onToPageChange(e.target.value);
                        }}
                        onKeyDown={(e) => {
                           if (e.key === 'Enter') {
                              void transactionsStore.goToPage();
                           }
                        }}
                        size="3"
                        color="blue"
                        type="number"
                        min={1}
                        max={transactionsStore.totalPages}
                     />
                  </Flex>
               </Flex>
            </Box>
         </Box>

         <TransactionDetailsDialog />
      </>
   );
}

export const Transactions = observer(TransactionsView);
